import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { DirectDebitNotificationReportDto, DirectDebitNotificationResultMember } from "../../types/membersTypes";
import { getDirectDebitFailureReasonLabel } from "../../utils/financeUtils";
import { downloadCsvFile } from "../../utils/csvUtils";

type DirectDebitNotificationReportDialogProps = {
  open: boolean;
  onClose: () => void;
  report: DirectDebitNotificationReportDto | null;
  failedCsvExportData?: object[];
  failedCsvFileName?: string;
};

const formatMemberLabel = (member: DirectDebitNotificationResultMember): string => {
  const name = [member.firstName, member.lastName].filter(Boolean).join(" ");
  return name || "Unbekanntes Mitglied";
};

/**
 * Shows the outcome of sending SEPA direct debit notification emails.
 */
const DirectDebitNotificationReportDialog: React.FunctionComponent<DirectDebitNotificationReportDialogProps> = ({
  open,
  onClose,
  report,
  failedCsvExportData,
  failedCsvFileName,
}) => {
  if (!report) {
    return null;
  }

  const handleDownloadFailedCsv = () => {
    if (!failedCsvExportData || failedCsvExportData.length === 0 || !failedCsvFileName) {
      return;
    }
    downloadCsvFile(failedCsvExportData, failedCsvFileName, { includeHeaders: false });
  };

  return (
    <Dialog open={open} disableEscapeKeyDown fullWidth maxWidth="sm">
      <DialogTitle>Benachrichtigungsbericht</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Nicht alle ausgewählten Mitglieder konnten benachrichtigt werden. Bitte prüfe die Liste und informiere
          Betroffene ggf. manuell.
        </Typography>

        {report.notified.length > 0 && (
          <Accordion disableGutters elevation={0} sx={{ mt: 1, "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1" fontWeight="bold">
                Erfolgreich benachrichtigt ({report.notified.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <List dense>
                {report.notified.map((member) => (
                  <ListItem key={`notified-${member.memberId}`} disableGutters>
                    <ListItemText primary={formatMemberLabel(member)} />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        )}

        <Accordion defaultExpanded disableGutters elevation={0} sx={{ mt: 1, "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" fontWeight="bold">
              Nicht erreichbar ({report.failed.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <List dense>
              {report.failed.map((member) => (
                <ListItem key={`failed-${member.memberId}`} disableGutters>
                  <ListItemText
                    primary={formatMemberLabel(member)}
                    secondary={getDirectDebitFailureReasonLabel(member.reason)}
                  />
                </ListItem>
              ))}
            </List>
            {report.failed.length > 0 && failedCsvExportData && failedCsvExportData.length > 0 && (
              <Button onClick={handleDownloadFailedCsv} color="primary" variant="contained" sx={{ mt: 2 }}>
                Nicht-Benachrichtigte Mitglieder Herunterladen
              </Button>
            )}
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DirectDebitNotificationReportDialog;
